/**
 * A referential of names (fabric books, sewing patterns): a list with add, edit
 * and delete. Admin only.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleNotch,
  faPencilAlt,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { MTM_ROUTE_PATH, PLATFORM, ROLES } from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { snackbar } from '@/shared/ui/snackbar';
import { useDeleteItem, useSavedDialogClose } from '../use-referential';
import plusIcon from '@/assets/images/plus.svg';
import type { NamedItem, NamedItemService } from './api';
import { NamedItemDialog } from './NamedItemDialog';
import { kebabCase } from '@/shared/lib/kebab-case';

const MIN_ROWS = 5;

export interface NamedItemsPageProps {
  service: NamedItemService;
  /** Accessible name of the list ("Fabric books"). */
  listLabel: string;
  /** Pads the table to five rows (the fabric books do, the sewing patterns do not). */
  showEmptyRows?: boolean;
  /** Name pre-filled in the creation dialog. */
  initialName?: string;
}

export function NamedItemsPage({
  service,
  listLabel,
  showEmptyRows = false,
  initialName,
}: Readonly<NamedItemsPageProps>) {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate(`/${MTM_ROUTE_PATH}/main`);
  }, [user, isAdmin]);

  const list = useQuery({
    queryKey: service.queryKey,
    queryFn: service.list,
    retry: false,
    enabled: isAdmin,
  });
  useEffect(() => {
    if (list.isError)
      snackbar(`Failed to load ${service.label}s, please try again later`);
  }, [list.isError, service.label]);

  const [dialog, setDialog] = useState<{ item: NamedItem | null } | null>(null);
  const [removing, setRemoving] = useState<NamedItem | null>(null);
  const items = list.data ?? [];

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: service.queryKey });

  const remove = useDeleteItem(
    (item: NamedItem) => service.delete(item.id),
    refresh,
  );

  const closeDialog = useSavedDialogClose(() => setDialog(null), refresh);

  if (!isAdmin) return null;

  const emptyRows = showEmptyRows ? Math.max(0, MIN_ROWS - items.length) : 0;
  const slug = kebabCase(service.label);
  const testIdPrefix = `mtm-${slug}`;

  return (
    <div className="container summary">
      <TopMenu platform={PLATFORM.DMS} />
      <div className="summary-table my-12">
        <button
          type="button"
          className="add-btn mb-4 flex items-center justify-center self-center"
          data-testid={`header-add-${slug}`}
          onClick={() => setDialog({ item: null })}
        >
          <img src={plusIcon} alt="" />
          <span className="pl-2">Add a {service.label}</span>
        </button>
        <div className="table-container">
          <div className="items-container" role="region" aria-label={listLabel}>
            <table className="table items">
              <thead>
                <tr>
                  <th style={{ width: '80%' }}>Name</th>
                  <th style={{ width: '20%' }} className="edit-header">
                    actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.isPending ? (
                  <tr>
                    <td colSpan={2} className="py-12 text-center">
                      <FontAwesomeIcon
                        icon={faCircleNotch}
                        spin
                        size="2x"
                        className="text-muted"
                      />
                      <div className="text-muted mt-2">Loading items...</div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {items.map((item) => (
                      <tr key={item.id} className="user-row">
                        <td
                          style={{
                            width: '80%',
                            textAlign: 'left',
                            paddingLeft: 30,
                          }}
                          title={item.name}
                          data-testid={`${testIdPrefix}-row-name`}
                        >
                          {item.name}
                        </td>
                        <td style={{ width: '20%' }} className="edit-action">
                          <button
                            type="button"
                            className="action-icon"
                            aria-label={`Edit ${item.name}`}
                            data-testid={`${testIdPrefix}-row-edit-button`}
                            onClick={() => setDialog({ item })}
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button
                            type="button"
                            className="action-icon ml-2"
                            aria-label={`Delete ${item.name}`}
                            data-testid={`${testIdPrefix}-row-delete-button`}
                            onClick={() => setRemoving(item)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {Array.from({ length: emptyRows }, (_, index) => (
                      <tr key={`empty-${index}`} className="empty-row">
                        <td style={{ width: '80%' }} />
                        <td style={{ width: '20%' }} />
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {dialog && (
        <NamedItemDialog
          key={dialog.item?.id ?? 'new'}
          service={service}
          item={dialog.item}
          initialName={initialName}
          testIdPrefix={testIdPrefix}
          onClose={closeDialog}
        />
      )}
      {removing !== null && (
        <ConfirmDialog
          title="Are you sure?"
          text="This action cannot be undone. This item will be deleted immediately."
          onConfirm={() => {
            if (removing) remove.mutate(removing);
            setRemoving(null);
          }}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}
